import notificationWithIcon from '@/utils/notification';
import apiCall from '@/store/apiCall';
import URLS from '@/store/urls';
import types from './types';

const actions = {
  async getCollections({ commit }, payload) {
    try {
      commit(types.GET_COLLECTIONS_START);
      const { data: { data } } = await apiCall({
        type: 'GET',
        url: URLS.pageCollections,
        params: payload,
        isToken: true,
      });
      commit(types.GET_COLLECTIONS_SUCCESS, data);
    } catch (e) {
      const { response: { data: { message } } = { data: { message: e.message } } } = e;
      notificationWithIcon('error', message);
      commit(types.GET_COLLECTIONS_FAILURE, message);
    }
  },
  async getCompanyCollections({ commit }, companyId) {
    try {
      commit(types.GET_COLLECTIONS_START);
      const { data: { data } } = await apiCall({
        type: 'GET',
        url: `${URLS.companies}/${companyId}/collections`,
        isToken: true,
      });
      commit(types.GET_COMPANY_COLLECTIONS_SUCCESS, data);
    } catch (e) {
      const { response: { data: { message } } = { data: { message: e.message } } } = e;
      notificationWithIcon('error', message);
      commit(types.GET_COLLECTIONS_FAILURE, message);
    }
  },
  async getCollectionDetails({ commit, dispatch }, { id, frameInfo }) {
    try {
      commit(types.GET_COLLECTION_DETAILS_START);
      const { data: { data } } = await apiCall({
        type: 'GET',
        url: `${URLS.pageCollections}/${id}`,
        isToken: true,
      });
      if (frameInfo) {
        const { products } = data;
        const product = products.find((item) => item.id === frameInfo.frameId);
        dispatch('updateFrameCollectionInfo', {
          ...frameInfo,
          id: product.refProductId,
        });
      } else {
        commit(types.GET_COLLECTION_DETAILS_SUCCESS, data);
      }
    } catch (e) {
      const { response: { data: { message } } = { data: { message: e.message } } } = e;
      notificationWithIcon('error', message);
      commit(types.GET_COLLECTION_DETAILS_FAILURE);
    }
  },
  async updateCollectionDetails({ dispatch }, payload) {
    try {
      const { id, value, pageKey } = payload;
      await apiCall({
        type: 'PATCH',
        url: `${URLS.pageCollections}/${id}`,
        body: value,
        isToken: true,
      });
      if (pageKey) {
        dispatch('getCollections', { pageKey });
      } else {
        dispatch('getCollectionDetails', { id });
      }
      notificationWithIcon('success', 'Collection updated');
    } catch (e) {
      const { response: { data: { message } } = { data: { message: e.message } } } = e;
      notificationWithIcon('error', message);
    }
  },
  async updateCollectionImage({ dispatch }, payload) {
    try {
      const { id, file } = payload;
      const frameImageData = new FormData();
      frameImageData.append('file', file);
      frameImageData.append('folder', 'collections-images');
      const { data: { data: { url } } } = await apiCall({
        type: 'POST',
        url: URLS.fileUpload,
        headers: { 'content-type': 'multipart/form-data' },
        body: frameImageData,
        isToken: true,
      });
      await apiCall({
        type: 'PATCH',
        url: `${URLS.pageCollections}/${id}`,
        body: {
          previewImage: url,
        },
        isToken: true,
      });
      dispatch('getCollectionDetails', { id });
      notificationWithIcon('success', 'Collection updated');
    } catch (e) {
      const { response: { data: { message } } = { data: { message: e.message } } } = e;
      notificationWithIcon('error', message);
    }
  },
  async deleteFrameFromCollection({ dispatch }, payload) {
    try {
      const { collectionId, ids } = payload;
      await apiCall({
        type: 'DELETE',
        url: `${URLS.pageCollections}/items`,
        params: {
          ids: Array.isArray(ids) ? ids.join(',') : ids,
        },
        isToken: true,
      });
      dispatch('getCollectionDetails', { id: collectionId });
      notificationWithIcon('success', 'Collection updated');
    } catch (e) {
      const { response: { data: { message } } = { data: { message: e.message } } } = e;
      notificationWithIcon('error', message);
    }
  },
  async updateCollectionFrameOptions({ dispatch }, payload) {
    const { value, frameInfo } = payload;
    try {
      await apiCall({
        type: 'POST',
        url: `${URLS.pageCollections}/items`,
        body: value,
        isToken: true,
      });
      dispatch('getCollectionDetails', { id: value.productCollectionId, frameInfo });
      notificationWithIcon('success', 'Collection frame updated');
    } catch (e) {
      const { response: { data: { message } } = { data: { message: e.message } } } = e;
      notificationWithIcon('error', message);
    }
  },
  async createCollection({ dispatch }, payload) {
    try {
      await apiCall({
        type: 'POST',
        url: URLS.pageCollections,
        body: payload,
        isToken: true,
      });
      dispatch('getCollections', { pageKey: payload.pageKey });
      dispatch('getPagesList');
      notificationWithIcon('success', `Collection ${payload.name} created`);
    } catch (e) {
      const { response: { data: { message } } = { data: { message: e.message } } } = e;
      notificationWithIcon('error', message);
    }
  },
  async deleteCollection({ dispatch }, payload) {
    try {
      const { name, pageKey, id } = payload;
      await apiCall({
        type: 'DELETE',
        url: URLS.pageCollections,
        params: { id },
        isToken: true,
      });
      dispatch('getCollections', { pageKey });
      notificationWithIcon('success', `Collection ${name} deleted`);
    } catch (e) {
      const { response: { data: { message } } = { data: { message: e.message } } } = e;
      notificationWithIcon('error', message);
    }
  },
  async updateFrameCollectionInfo({ dispatch }, payload) {
    try {
      const {
        name,
        productCollectionId,
        data,
        id,
      } = payload;
      await apiCall({
        type: 'PUT',
        url: `${URLS.pageCollections}/item/${id}`,
        body: data,
        isToken: true,
      });
      dispatch('getCollectionDetails', { id: productCollectionId });
      notificationWithIcon('success', `${name} info updated`);
    } catch (e) {
      const { response: { data: { message } } = { data: { message: e.message } } } = e;
      notificationWithIcon('error', message);
    }
  },
  async getPagesList({ commit }) {
    try {
      commit(types.GET_COLLECTIONS_START);
      const { data: { data } } = await apiCall({
        type: 'GET',
        url: `${URLS.pageCollections}/all/keys`,
        isToken: true,
      });
      commit(types.GET_COLLECTIONS_PAGES_SUCCESS, data);
    } catch (e) {
      const { response: { data: { message } } = { data: { message: e.message } } } = e;
      notificationWithIcon('error', message);
    }
  },
  async updateCollectionsPositions({ dispatch }, payload) {
    try {
      const { pageKey, collections } = payload;
      await apiCall({
        type: 'PUT',
        url: `${URLS.pageCollections}/change-position`,
        body: collections,
        isToken: true,
      });
      dispatch('getCollections', { pageKey });
      notificationWithIcon('success', 'Collections positions updated');
    } catch (e) {
      const { response: { data: { message } } = { data: { message: e.message } } } = e;
      notificationWithIcon('error', message);
    }
  },
  async updateFramesCollectionPositions({ dispatch }, payload) {
    try {
      const {
        changePosition,
        productCollectionId,
      } = payload;
      await apiCall({
        type: 'PUT',
        url: `${URLS.product}/frame/position`,
        body: {
          changePosition,
        },
        isToken: true,
      });
      dispatch('getCollectionDetails', { id: productCollectionId });
      notificationWithIcon('success', 'Positions updated');
    } catch (e) {
      const { response: { data: { message } } = { data: { message: e.message } } } = e;
      notificationWithIcon('error', message);
    }
  },
};
export default actions;
